import { APIComponentAttributes, ArdoqId } from '@ardoq/api-types';
import { Button, BUTTON_SELECTOR, ButtonType } from '@ardoq/button';
import { colors } from '@ardoq/design-tokens';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  MenuItemDef,
} from 'ag-grid-enterprise';
import { createComponentsOperations } from './createComponents/createComponentsOperations';
import { InventoryContext } from './types';
import {
  componentButtonProps,
  ComponentButtonProps,
} from './componentButtonProps';
import { inventoryGridAPIOperations } from './inventoryGridAPIOperations';
import { refreshServerSideForGrid } from './serverSideRefresh';
import styled from 'styled-components';
import { isTruthy } from '@ardoq/common-helpers';
import { WithPopover } from '@ardoq/popovers';

const toContextMenuItemDefinition = (
  props: ComponentButtonProps
): MenuItemDef<APIComponentAttributes> => ({
  name: props.name,
  menuItemParams: {
    iconName: props.iconName,
    tooltip: props.tooltip,
  },
  menuItem: CustomMenuButton,
  action: props.onClick,
  disabled: props.isDisabled,
});

export const getContextMenuItems = (
  params: GetContextMenuItemsParams<APIComponentAttributes, InventoryContext>
): (DefaultMenuItem | MenuItemDef<APIComponentAttributes>)[] => {
  if (
    !params.context ||
    params.context.config.isContextMenuDisabled ||
    params.context.config.mode === 'filterData'
  ) {
    return [];
  }
  const gridApi = params.api;

  const filteredOutDefaultItems = filterOutDefaultItems(
    params.defaultItems ?? []
  );
  if (
    !gridApi ||
    !params.node?.data ||
    createComponentsOperations.isComponentNotPersisted(params.node?.data._id)
  ) {
    return filteredOutDefaultItems;
  }

  /**
   * When user selects the first component and scrolls to the
   * bottom of the table of a large workspace/component type, some pages
   * are not loaded, so the components array can contain undefined values.
   * This `filter(isTruthy)` ensures that we at least don't crush,
   * but we need a special UI for large datasets.
   */
  const selectedComponents = gridApi.getSelectedRows().filter(isTruthy);
  const isTargetComponentSelected = params.node.isSelected() ?? false;
  const targetedComponents = isTargetComponentSelected
    ? selectedComponents
    : [params.node.data]; // If the target component is not already selected, we only use the targeted component for the action. This is consistent with how selection works in the asset manager and in the workspace mode navigation sidebar
  const sharedButtonProps = {
    components: targetedComponents,
    accessControl: params.context.accessControl,
    scopeData: params.context.scopeData,
    commands: params.context.commands,
    // AG Grid external-lib callback: read locale from context so the menu uses
    // the latest value without putting i18nLocale in any hook dep array. See
    // apps/ardoq-front/AGENTS.md.
    i18nLocale: params.context.i18nLocale ?? {},
    isLockingEnabled: params.context.isLockingEnabled,
  };
  const sharedAgGridButtonProps = {
    agGridDeselectAll: () => gridApi.deselectAll(),
    agGridServerSideRefresh: () => refreshServerSideForGrid(gridApi),
    agGridRemovePinnedRows: (componentIds: ArdoqId[]) =>
      inventoryGridAPIOperations.removeComponentsFromPinnedRows(
        gridApi,
        componentIds
      ),
  };

  const dataSourceType = params.context.datasourceSelection.dataSourceType;

  const hierarchyMenuItems = [
    toContextMenuItemDefinition(
      componentButtonProps.getAddChildButtonProps(sharedButtonProps)
    ),
    toContextMenuItemDefinition(
      componentButtonProps.getChangeParentButtonProps(sharedButtonProps)
    ),
  ];

  const scenarioMenuItems = [
    componentButtonProps.getCreateScenarioButtonProps(sharedButtonProps),
    componentButtonProps.getAddToScenarioButtonProps(sharedButtonProps),
  ]
    .filter(isTruthy)
    .map(buttonProps => toContextMenuItemDefinition(buttonProps));

  const standardActionMenuItems = [
    ...[
      componentButtonProps.getDuplicateButtonProps({
        ...sharedButtonProps,
        ...sharedAgGridButtonProps,
        dataSourceType,
      }),
      componentButtonProps.getMergeButtonProps(sharedButtonProps),
      componentButtonProps.getViewHistoryButtonProps(sharedButtonProps),
    ].map(buttonProps => toContextMenuItemDefinition(buttonProps)),
    ...scenarioMenuItems,
    'separator' as DefaultMenuItem,
    toContextMenuItemDefinition(
      componentButtonProps.getDeleteButtonProps({
        ...sharedButtonProps,
        ...sharedAgGridButtonProps,
      })
    ),
  ];

  const contextMenuItems: (
    | DefaultMenuItem
    | MenuItemDef<APIComponentAttributes>
  )[] = [
    ...(params.context.hierarchy.isHierarchyEnabled
      ? [...hierarchyMenuItems, 'separator' as DefaultMenuItem]
      : []),

    ...standardActionMenuItems,
  ];

  return [...filteredOutDefaultItems, ...contextMenuItems];
};

const omittedAgGridDefaultItems: DefaultMenuItem[] = [
  'copy',
  'copyWithHeaders',
  'copyWithGroupHeaders',
  'paste',
  'cut',
  'separator',
  'export',
  'chartRange',
  'pivotChart',
];

const filterOutDefaultItems = (
  defaultItems: DefaultMenuItem[]
): DefaultMenuItem[] => {
  return defaultItems.filter(item => !omittedAgGridDefaultItems.includes(item));
};

type CustomMenuButtonItemProps = { iconName: IconName; tooltip?: string };

type CustomMenuButtonProps = MenuItemDef<APIComponentAttributes> & {
  menuItemParams: CustomMenuButtonItemProps;
  closeMenu: () => void; // provided by AG Grid
  action: () => void;
};

const CustomMenuButton = ({
  name,
  action,
  closeMenu,
  disabled,
  menuItemParams,
}: CustomMenuButtonProps) => {
  const { iconName, tooltip } = menuItemParams;
  const handleClick = () => {
    action();
    closeMenu();
  };

  return (
    <WithPopover content={disabled ? tooltip : undefined}>
      <ButtonWrapper>
        <Button
          buttonType={ButtonType.GHOST}
          onClick={handleClick}
          isDisabled={disabled}
        >
          <Icon
            iconName={iconName}
            iconSize={IconSize.SMALL}
            color={colors.icon}
          />
          {name}
        </Button>
      </ButtonWrapper>
    </WithPopover>
  );
};

const ButtonWrapper = styled.div`
  width: 100%;
  ${BUTTON_SELECTOR} {
    width: 100%;
  }
`;
